import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { filter, takeUntil } from 'rxjs/operators';
import { BackgroundScrollService, ConfirmationService } from '../../services';
import { Confirmation, ConfirmationConfig, ConfirmationType } from '../../models';
import { IconType } from '@shared-features/enums/shared-enums.enum';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FormGroup, FormBuilder, FormControl, Validators } from '@angular/forms';
import { BaseComponent } from '@shared-features/components/base-component/base.component';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-confirmation',
  templateUrl: './confirmation.component.html',
  styleUrls: ['./confirmation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class ConfirmationComponent extends BaseComponent implements OnInit, OnDestroy {
  @ViewChild('dialogTemplate') dialogTemplate: TemplateRef<any>;
  checkboxValue = false;
  confirmData: ConfirmationConfig = new Confirmation();

  protected readonly IconType = IconType;
  protected readonly ConfirmationType = ConfirmationType;
  private dispose$ = new Subject();
  private dialogRefs: { [id: string]: MatDialogRef<any> } = {};
  form: FormGroup;

  constructor(
    private confirmationService: ConfirmationService,
    private backgroundScrollService: BackgroundScrollService,
    private dialogService: MatDialog,
    private changeDetectorRef: ChangeDetectorRef,
    private formBuilder: FormBuilder
  ) {
    super();
  }

  ngOnDestroy(): void {
    this.dispose$.next(true);
    this.dispose$.complete();
  }

  onClickReject(dialogId) {
    this.confirmationService.onConfirmMessageAction(false, false);
    this.dialogRefs[dialogId]?.close();
    this.backgroundScrollService.setScrollState(true);
    this.changeDetectorRef.markForCheck();
  }

  onClickConfirm(dialogId, checkboxValue) {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.form.updateValueAndValidity();
      return;
    }
    const textAreaValueToSend = this.form.value.textarea?.trim();
    this.confirmationService.setTextareaValue(textAreaValueToSend);
    this.confirmationService.onConfirmMessageAction(true, checkboxValue);
    this.dialogRefs[dialogId]?.close();
    this.backgroundScrollService.setScrollState(true);
    this.form?.get('textarea')?.setValue(null);
    this.changeDetectorRef.markForCheck();
  }

  ngOnInit() {
    this.confirmationService.confirmationMessage$
      .pipe(
        takeUntil(this.dispose$),
        filter((confirmation) => confirmation !== null)
      )
      .subscribe((confirm) => {
        const refUniqueId = Date.now().toString(32) + Math.random().toString(32).substr(2);
        this.confirmData = confirm;
        this.backgroundScrollService.setScrollState(false);

        this.dialogRefs[refUniqueId] = this.dialogService.open(this.dialogTemplate, {
          data: {
            ConfirmationType,
            ...confirm,
            reject: () => this.onClickReject(refUniqueId),
            confirm: (checkboxValue) => this.onClickConfirm(refUniqueId, checkboxValue),
          },
          panelClass: 'confirmation-dialog-panel',
          backdropClass: 'confirmation-dialog-backdrop',
          autoFocus: false,
          disableClose: this.confirmData.disableCloseDialog,
        });
        this.initForm();
      });
  }
  private initForm(): void {
    this.form = this.formBuilder.group({});
    if (this.confirmData.textAreaText) {
      this.form.registerControl(
        'textarea',
        new FormControl('', [...(this.confirmData?.isTextAreaRequired ? [Validators.required, this.noSpacesValidator] : [])])
      );
    }
    if (this.confirmData.checkboxOption) {
      this.form.registerControl('checkboxOption', new FormControl(false));
    }
  }

  private noSpacesValidator(control: FormControl) {
    const hasSpaces = control.value?.trim();

    return hasSpaces === '' ? { noSpaces: true } : null;
  }
}
